// @mui material components
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Stack,
  Switch,
  Button,
  GlobalStyles,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { ReactComponent as OccupancyIcon } from '../../assets/images/icons/OCCUPANCY.svg';
import Box from '@mui/material/Box';
import SensorsIcon from '@mui/icons-material/Sensors';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import Sensor from 'assets/images/sensor.png';
import { ReactComponent as ControllerBoxIcon } from 'assets/images/icons/controller-box.svg';
import { ReactComponent as Fan } from 'assets/images/icons/fan-speed.svg';
import DevicesListFilter from 'components/DevicesFilter/DevicesListFilter.js';
import MarqueeText from 'components/DevicesFilter/MarqueeText';
import DropdownMenuVertical from 'components/DropdownMenuVertical';
import CustomSnackbar from 'components/CustomSnackbar';
import GenericModal from 'components/GenericModal';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import DashboardLayout from 'layouts/LayoutContainers/DashboardLayout';
import ReportDeviceModalContent from './ReportDeviceModalContent';
import { useEffect, useLayoutEffect, useMemo, useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { brandColors } from 'types/colors';
import {
  fetchUserDevices,
  getBuildings,
  updateFilteredDevicesWithSync,
  setSelectedFloor,
  setSelectedStatus,
} from '../../stores/sensor/sensorStore';
import EmptyStateMessage from '../../components/MessageBox/EmptyStateMessage';
import { useMediaQuery, useTheme } from '@mui/material';

// Import filter actions and helpers
import { setCurrentPage } from '../../stores/sensor/sensorStore';
import zIndex from '@mui/material/styles/zIndex';

const REFRESH_TIME_IN_SECONDS = 300;
const INACTIVE_TIME_IN_SECONDS = 900;
const MAX_SELECTED_FLOORS = 6;
const apiUrl = process.env.REACT_APP_BACKEND_URL;

const buildFloorRows = floorTiles => {
  switch (floorTiles.length) {
    case 0:
      return [];
    case 1:
      return [floorTiles];
    case 2:
      return [floorTiles];
    case 3:
      return [floorTiles.slice(0, 2), floorTiles.slice(2, 3)];
    case 4:
      return [floorTiles.slice(0, 2), floorTiles.slice(2, 4)];
    case 5:
      return [floorTiles.slice(0, 3), floorTiles.slice(3, 5)];
    default:
      return [floorTiles.slice(0, 3), floorTiles.slice(3, 6)];
  }
};

export const fetchS3ImageUrl = async (imageKey, apiBaseUrl = apiUrl) => {
  if (!imageKey) {
    return null;
  }

  const response = await fetch(`${apiBaseUrl}upload/download-url?key=${encodeURIComponent(imageKey)}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch image URL: ${response.status}`);
  }

  const data = await response.json();

  if (data.success && data.downloadURL) {
    return data.downloadURL;
  }

  throw new Error('Invalid response format');
};

function capitalizeFirstLetter(inputString) {
  if (!inputString) return inputString; // Handle empty strings or null/undefined
  return inputString.charAt(0).toUpperCase() + inputString.slice(1);
}

function formatUnixSecondsToDisplay(unixSeconds) {
  const value = Number(unixSeconds);
  if (!Number.isFinite(value) || value <= 0) {
    return 'N/A';
  }

  const date = new Date(value * 1000);
  if (Number.isNaN(date.getTime())) {
    return 'N/A';
  }

  const pad = num => String(num).padStart(2, '0');
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = String(date.getFullYear()).slice(-2);

  return `${hours}:${minutes}, ${day}.${month}.${year}`;
}

function getControlModeLabel(control) {
  if (control === 'solai') return 'SOLAI';
  if (control === 'schedule') return 'schedule';
  if (control === 'manual') return 'manual';
  return 'N/A';
}

function formatDeviceNameWithLineBreaks(name, maxCharsPerLine = 16) {
  if (!name) {
    return name;
  }

  const safeName = String(name);
  if (safeName.length <= maxCharsPerLine) {
    return safeName;
  }

  const chunks = safeName.match(new RegExp(`.{1,${maxCharsPerLine}}`, 'g'));
  return chunks ? chunks.join('\n') : safeName;
}

const isHiddenInFloorMap = device => Number(device?.map_x_value) === -1 && Number(device?.map_y_value) === -1;

export const Measurements = [
  'clean_air_level_min',
  'co2_ratio_min',
  'comfort_level_avg',
  'co2_max',
  'rh_avg',
  'temperature_avg',
  'mass_pm_2_5_max',
  'mass_pm_10_max',
  'tvoc_avg',
  'motion_counter',
  'occupancy',
];

const MEASUREMENT_LABELS = {
  clean_air_level_min: 'Clean air level',
  co2_ratio_min: 'Ventilation level',
  comfort_level_avg: 'Comfort level',
  co2_max: 'Co2',
  rh_avg: 'Rh',
  temperature_avg: 'Temperature',
  mass_pm_2_5_max: 'Pm 2.5',
  mass_pm_10_max: 'Pm 10',
  tvoc_avg: 'Tvoc',
  motion_counter: 'Motion',
  occupancy: 'Occupancy',
  thermal_justified: 'Thermal Justified',
  air_justified: 'Air Justified',
  thermal_requested: 'Thermal Requested',
  air_requested: 'Air Requested',
  thermal_saveable: 'Thermal Saveable',
  air_saveable: 'Air Saveable',
};

const measurementsOpts = Measurements.map(meas => {
  return {
    value: meas,
    name: MEASUREMENT_LABELS[meas],
  };
});

export const demandMeasurements = [
  { field: 'occupancy', label: 'Occupancy', color: brandColors.BLACK },
  { field: 'thermal_justified', label: 'Thermal justified', color: brandColors.BLUE },
  { field: 'thermal_saveable', label: 'Thermal saveable', color: brandColors.GREEN },
  { field: 'thermal_requested', label: 'Thermal requested', color: brandColors.RED },
  { field: 'air_justified', label: 'Air justified', color: brandColors.BLUE },
  { field: 'air_saveable', label: 'Air saveable', color: brandColors.GREEN },
  { field: 'air_requested', label: 'Air requested', color: brandColors.RED },
];

// Options for how fast the map rotates between selected measurements.
const measurementRotationOptions = [5, 10, 20, 30, 60].map(seconds => {
  return {
    value: seconds,
    name: `${seconds} seconds`,
  };
});

// Convert a measurement field name into the user-facing label.
const getMeasurementLabel = measurement => MEASUREMENT_LABELS[measurement] || measurement;

const FloorsMap = () => {
  const [showGrid, setShowGrid] = useState(false);
  const currentUser = useSelector(state => state.sensors.currentUser);
  // The user can select many measurements, but the map displays one at a time.
  const [selectedMeasurementTypes, setSelectedMeasurementTypes] = useState([measurementsOpts[0].value]);
  const [selectedMeasurements, setSelectedMeasurements] = useState([measurementsOpts[0].value]);
  const [selectedDemandMeasurements, setSelectedDemandMeasurements] = useState([]);
  // This index points to the selected measurement currently shown on the map.
  const [displayedMeasurementIndex, setDisplayedMeasurementIndex] = useState(0);
  const [measurementRotationSeconds, setMeasurementRotationSeconds] = useState(measurementRotationOptions[0].value);
  const measurementSelectRef = useRef(null);
  const buildingDemandSelectRef = useRef(null);
  const [measurementMenuWidth, setMeasurementMenuWidth] = useState(0);
  const [buildingDemandMenuWidth, setBuildingDemandMenuWidth] = useState(0);
  // The rest of the map uses this single current measurement value.
  const displayedMeasurementType = selectedMeasurementTypes[displayedMeasurementIndex] || measurementsOpts[0].value;
  const displayedMeasurementLabel = getMeasurementLabel(displayedMeasurementType);
  const displayedMeasurementLabelColor = displayedMeasurementType.endsWith('_saveable')
    ? brandColors.GREEN
    : displayedMeasurementType.endsWith('_justified')
      ? brandColors.BLUE
      : displayedMeasurementType.endsWith('_requested')
        ? brandColors.RED
        : '#111';
  const [floorImageUrlsByFloor, setFloorImageUrlsByFloor] = useState({});
  const [floorImageAspectRatiosByFloor, setFloorImageAspectRatiosByFloor] = useState({});
  const [deviceType, setDeviceType] = useState('sensors');
  const [isReportMode, setIsReportMode] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [selectedReportDeviceId, setSelectedReportDeviceId] = useState(null);
  const [selectedReportDeviceName, setSelectedReportDeviceName] = useState('');
  const [reportOccupancy, setReportOccupancy] = useState('');
  const [reportComment, setReportComment] = useState('');
  const [reportSnackbar, setReportSnackbar] = useState({
    open: false,
    severity: 'info',
    message: '',
  });
  const [selectedFloorsForMap, setSelectedFloorsForMap] = useState([]);
  const [hoveredDeviceId, setHoveredDeviceId] = useState(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobileLandscape = useMediaQuery('(orientation: landscape) and (max-height:600px)');
  const isScrollableMobileMap = isSmallScreen || isMobileLandscape;
  const isMediumUp = useMediaQuery(theme.breakpoints.up('md'));
  const isMultiFloorEnabled = isMediumUp;

  const devices = useSelector(state => state.sensors.devices);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [imageLoading, setImageLoading] = useState(false);

  useLayoutEffect(() => {
    const measurementElement = measurementSelectRef.current;
    const buildingDemandElement = buildingDemandSelectRef.current;
    if (!measurementElement || !buildingDemandElement) return undefined;
    let animationFrameId = null;

    const updateWidth = () => {
      const measurementWidth = Math.round(measurementElement.getBoundingClientRect().width);
      const buildingDemandWidth = Math.round(buildingDemandElement.getBoundingClientRect().width);
      setMeasurementMenuWidth(currentWidth => (currentWidth === measurementWidth ? currentWidth : measurementWidth));
      setBuildingDemandMenuWidth(currentWidth => (currentWidth === buildingDemandWidth ? currentWidth : buildingDemandWidth));
    };

    const scheduleWidthUpdate = () => {
      if (animationFrameId !== null) return;
      animationFrameId = requestAnimationFrame(() => {
        animationFrameId = null;
        updateWidth();
      });
    };

    updateWidth();
    const observer = new ResizeObserver(scheduleWidthUpdate);
    observer.observe(measurementElement);
    observer.observe(buildingDemandElement);
    return () => {
      observer.disconnect();
      if (animationFrameId !== null) cancelAnimationFrame(animationFrameId);
    };
  }, [deviceType]);

  // Redux filter state
  const { selectedFilterObject } = useSelector(state => state.persistence);
  const { building, floor, area, status, hvacEquipment } = selectedFilterObject;
  const buildings = useSelector(state => state.users.buildings);
  const isDeviceFocusActive = hoveredDeviceId !== null;
  // Set current page for navigation tracking
  useEffect(() => {
    dispatch(setCurrentPage('floors-map'));
  }, [dispatch]);

  useEffect(() => {
    dispatch(setSelectedStatus('all'));
    dispatch(fetchUserDevices({ forMap: true, selectedFilterObject: selectedFilterObject }));
    // }
    dispatch(getBuildings());
    const interval = setInterval(() => {
      const dispatchAction = async () => {
        dispatch(fetchUserDevices({ forMap: true, selectedFilterObject: selectedFilterObject }));
        dispatch(updateFilteredDevicesWithSync(selectedFilterObject));
      };
      dispatchAction();
    }, REFRESH_TIME_IN_SECONDS * 1000);

    return () => clearInterval(interval);
  }, []);

  const handleDeviceTypeChange = (_, newDeviceType) => {
    if (newDeviceType !== null) {
      setDeviceType(newDeviceType);
    }
  };

  const handleMeasurementTypesChange = event => {
    // In a multi-select, event.target.value is the full selected values array.
    const value = event.target.value;
    const requestedMeasurements = typeof value === 'string' ? value.split(',') : value;
    const nextMeasurements =
      requestedMeasurements.length || selectedDemandMeasurements.length ? requestedMeasurements : [measurementsOpts[0].value];

    // A Measurement selection can be empty while Building demand has a selection.
    setSelectedMeasurements(nextMeasurements);
    setSelectedMeasurementTypes([...new Set([...nextMeasurements, ...selectedDemandMeasurements])]);
    // Start the rotation from the first selected measurement after every selection change.
    setDisplayedMeasurementIndex(0);
  };

  useEffect(() => {
    // If the selected list becomes shorter, make sure the display index is still valid.
    if (displayedMeasurementIndex >= selectedMeasurementTypes.length) {
      setDisplayedMeasurementIndex(0);
    }
  }, [displayedMeasurementIndex, selectedMeasurementTypes.length]);

  useEffect(() => {
    // Rotation is only needed when there is more than one sensor measurement selected.
    if (selectedMeasurementTypes.length <= 1 || deviceType === 'controllers') {
      return undefined;
    }

    const interval = setInterval(() => {
      // Move to the next selected measurement, then wrap back to the first one.
      setDisplayedMeasurementIndex(prevIndex => (prevIndex + 1) % selectedMeasurementTypes.length);
    }, measurementRotationSeconds * 1000);

    // Clear the old timer when selections, interval, or device type changes.
    return () => clearInterval(interval);
  }, [selectedMeasurementTypes.length, measurementRotationSeconds, deviceType]);

  const buildGlow = color => `drop-shadow(0 0 6px ${color}) drop-shadow(0 0 16px ${color}) drop-shadow(0 0 28px ${color})`;
  const buildGrayGlow = () =>
    `drop-shadow(0 0 6px rgba(128, 128, 128, 0.9)) drop-shadow(0 0 16px rgba(128, 128, 128, 0.75)) drop-shadow(0 0 28px rgba(128, 128, 128, 0.6))`;
  const buildBlueGlow = () =>
    `drop-shadow(0 0 6px rgba(70, 132, 255)) drop-shadow(0 0 16px rgba(70, 132, 255)) drop-shadow(0 0 28px rgba(70, 132, 255, 0.6))`;
  const buildControllerBottomShadow = () => `drop-shadow(0 7px 5px rgba(0, 0, 0, 0.45))`;
  const interpolateHexColor = (startColor, endColor, value) => {
    const intensity = Math.min(Math.max(value, 0), 100) / 100;
    const start = startColor.match(/\w\w/g).map(hex => parseInt(hex, 16));
    const end = endColor.match(/\w\w/g).map(hex => parseInt(hex, 16));

    return `#${start
      .map((channel, index) =>
        Math.round(channel + (end[index] - channel) * intensity)
          .toString(16)
          .padStart(2, '0'),
      )
      .join('')}`;
  };
  const buildDemandGlow = (color, measValue) => {
    const intensity = Math.min(Math.max(Number(measValue), 0), 100) / 100;
    const red = parseInt(color.slice(1, 3), 16);
    const green = parseInt(color.slice(3, 5), 16);
    const blue = parseInt(color.slice(5, 7), 16);
    const rgba = alpha => `rgba(${red}, ${green}, ${blue}, ${alpha})`;
    const coreBlur = Math.round(3 + intensity * 7);
    const middleBlur = Math.round(8 + intensity * 16);
    const outerBlur = Math.round(14 + intensity * 26);

    return [
      `drop-shadow(0 0 ${coreBlur}px ${rgba(0.35 + intensity * 0.65)})`,
      `drop-shadow(0 0 ${middleBlur}px ${rgba(0.2 + intensity * 0.6)})`,
      `drop-shadow(0 0 ${outerBlur}px ${rgba(0.1 + intensity * 0.45)})`,
    ].join(' ');
  };
  const buildMotionGlow = measValue => {
    const glowValue = Math.min(Math.max(measValue, 0), 50) * 2;
    return buildDemandGlow(interpolateHexColor(brandColors.GREY, brandColors.BLUE, glowValue), glowValue);
  };

  const getSensorGlow = measValue => {
    if (
      displayedMeasurementType.endsWith('_saveable') ||
      displayedMeasurementType.endsWith('_justified') ||
      displayedMeasurementType.endsWith('_requested')
    ) {
      if (!Number.isFinite(measValue) || measValue === -1) return buildGrayGlow();

      const demandColor = displayedMeasurementType.endsWith('_saveable')
        ? brandColors.GREEN
        : displayedMeasurementType.endsWith('_justified')
          ? brandColors.BLUE
          : brandColors.RED;
      return buildDemandGlow(demandColor, measValue);
    }

    switch (displayedMeasurementType) {
      case 'clean_air_level_min':
        if (measValue === -1) return buildGrayGlow();
        else if (measValue > 84) return buildGlow(brandColors.GREEN);
        else if (measValue > 69) return buildGlow(brandColors.YELLOW);
        else return buildGlow(brandColors.RED);
      case 'co2_ratio_min':
        if (measValue === -1) return buildGrayGlow();
        else if (measValue > 82) return buildGlow(brandColors.GREEN);
        else if (measValue > 69) return buildGlow(brandColors.YELLOW);
        else return buildGlow(brandColors.RED);
      case 'co2_max':
        if (measValue === -1) return buildGrayGlow();
        else if (measValue < 1001) return buildGlow(brandColors.GREEN);
        else if (measValue >= 1001 && measValue < 1199) return buildGlow(brandColors.YELLOW);
        else return buildGlow(brandColors.RED);
      case 'rh_avg':
        if (measValue === -1) return buildGrayGlow();
        else if (measValue < 25 || measValue > 70) return buildGlow(brandColors.RED);
        else if (measValue > 44 && measValue < 61) return buildGlow(brandColors.GREEN);
        else return buildGlow(brandColors.YELLOW);
      case 'temperature_avg':
        if (measValue === -1) return buildGrayGlow();
        else if (measValue > 28) return buildGlow(brandColors.RED);
        else if (measValue <= 21) return buildGlow(brandColors.TURQUOISE);
        else if (measValue > 21 && measValue < 26) return buildGlow(brandColors.GREEN);
        else return buildGlow(brandColors.ORANGE);
      case 'mass_pm_2_5_max':
        if (measValue === -1) return buildGrayGlow();
        else if (measValue < 12) return buildGlow(brandColors.GREEN);
        else if (measValue < 35) return buildGlow(brandColors.YELLOW);
        else return buildGlow(brandColors.RED);
      case 'mass_pm_10_max':
        if (measValue === -1) return buildGrayGlow();
        else if (measValue < 20) return buildGlow(brandColors.GREEN);
        else if (measValue < 50) return buildGlow(brandColors.YELLOW);
        else return buildGlow(brandColors.RED);
      case 'tvoc_avg':
        if (measValue === -1) return buildGrayGlow();
        else if (measValue < 191) return buildGlow(brandColors.GREEN);
        else if (measValue < 313) return buildGlow(brandColors.YELLOW);
        else return buildGlow(brandColors.RED);
      case 'comfort_level_avg':
        if (measValue === -1) return buildGrayGlow();
        else if (measValue > 85) return buildGlow(brandColors.GREEN);
        else if (measValue > 71) return buildGlow(brandColors.YELLOW);
        else return buildGlow(brandColors.RED);
      case 'occupancy':
        if (measValue === -1) return buildGrayGlow();
        else if (measValue === 0) return `drop-shadow(0 0 1px rgb(0, 0, 0))`;
        else if (measValue < 30)
          return `drop-shadow(0 0 8px rgba(70, 132, 255, 0.9)) drop-shadow(0 0 18px rgba(70, 132, 255, 0.5))`;
        else if (measValue < 70)
          return `drop-shadow(0 0 8px rgba(70, 132, 255)) drop-shadow(0 0 18px rgba(70, 132, 255, 0.6)) drop-shadow(0 0 30px rgba(70, 132, 255, 0.5))`;
        else
          return `drop-shadow(0 0 30px rgba(70, 132, 255)) drop-shadow(0 0 40px rgba(70, 132, 255)) drop-shadow(0 0 50px rgba(70, 132, 255))`;
      case 'motion_counter':
        if (!Number.isFinite(Number(measValue)) || Number(measValue) === -1) return buildGrayGlow();
        return buildMotionGlow(Number(measValue));
      default:
        return 'none';
    }
  };

  const getTemperatureStatusColor = measValue => {
    if (measValue === -1) return '#808080';
    if (measValue < 17 || measValue > 28) return brandColors.RED;
    if (measValue > 20 && measValue < 26) return brandColors.GREEN;
    return brandColors.ORANGE;
  };

  const getMeasUnit = type => {
    if (
      type === 'occupancy' ||
      type === 'clean_air_level_min' ||
      type === 'rh_avg' ||
      type === 'comfort_level_avg' ||
      type === 'co2_ratio_min' ||
      type === 'thermal_justified' ||
      type === 'air_justified' ||
      type === 'thermal_requested' ||
      type === 'air_requested' ||
      type === 'thermal_saveable' ||
      type === 'air_saveable' ||
      type === 'motion_counter'
    ) {
      return `%`;
    } else if (type === 'tvoc_avg') {
      return `(µg/m³)`;
    } else if (type === 'mass_pm_2_5_max' || type === 'mass_pm_10_max') {
      return `(µg/m³)`;
    } else if (type === 'temperature_avg') {
      return `°`;
    } else if (type === 'co2_max') {
      return `ppm`;
    } else {
      return '';
    }
  };

  const selectedBuilding = useMemo(() => {
    if (!building || building === 'all' || !buildings?.length) {
      return null;
    }
    return buildings.find(b => String(b.id) === String(building)) || null;
  }, [building, buildings]);

  const availableBuildingFloorNumbers = useMemo(() => {
    if (!selectedBuilding?.floors || !Array.isArray(selectedBuilding.floors)) {
      return [];
    }

    return selectedBuilding.floors
      .map(f => Number(f?.floor))
      .filter(Number.isFinite)
      .sort((a, b) => a - b)
      .map(String);
  }, [selectedBuilding]);

  const selectedFloorNumbers = useMemo(() => {
    const cleanedSelection =
      isMultiFloorEnabled && Array.isArray(selectedFloorsForMap)
        ? selectedFloorsForMap
            .map(String)
            .filter(value => value && value !== 'all')
            .slice(0, MAX_SELECTED_FLOORS)
        : [];

    if (cleanedSelection.length > 0) {
      return cleanedSelection;
    }

    if (floor && floor !== 'all') {
      return [String(floor)];
    }

    return [];
  }, [floor, selectedFloorsForMap, isMultiFloorEnabled]);

  const displayedDevices = useMemo(() => {
    if (!devices || devices.length === 0) {
      return [];
    }

    const buildingFilteredDevices =
      building && building !== 'all' ? devices.filter(device => String(device?.building_id ?? '') === String(building)) : devices;

    const floorFilteredDevices =
      selectedFloorNumbers.length > 0
        ? buildingFilteredDevices.filter(device =>
            selectedFloorNumbers.includes(String(device?.floor_number ?? device?.floor ?? '')),
          )
        : buildingFilteredDevices;
    const equipmentFilteredDevices =
      hvacEquipment && hvacEquipment !== 'all'
        ? floorFilteredDevices.filter(
            device =>
              Array.isArray(device?.hvac_equipment_ids) &&
              device.hvac_equipment_ids.some(id => String(id) === String(hvacEquipment)),
          )
        : floorFilteredDevices;
    const visibleFloorDevices = equipmentFilteredDevices.filter(device => !isHiddenInFloorMap(device));

    if (deviceType === 'controllers') {
      return visibleFloorDevices.filter(
        device => typeof device.device_type === 'string' && device.device_type.startsWith('Controller'),
      );
    }

    return visibleFloorDevices.filter(
      device => typeof device.device_type === 'string' && device.device_type.toLowerCase().includes('sensor'),
    );
  }, [devices, building, deviceType, hvacEquipment, selectedFloorNumbers]);

  const selectedFloorTiles = useMemo(() => {
    if (!selectedBuilding || selectedFloorNumbers.length === 0) {
      return [];
    }

    return selectedFloorNumbers.map(floorNumber => {
      const floorObj = selectedBuilding?.floors?.find(f => String(f.floor) === String(floorNumber)) || null;
      return {
        floorNumber: String(floorNumber),
        imageKey: floorObj?.scheme || null,
      };
    });
  }, [selectedBuilding, selectedFloorNumbers]);

  const floorTilesToRender = useMemo(
    () => selectedFloorTiles.filter(tile => floorImageUrlsByFloor[tile.floorNumber]),
    [selectedFloorTiles, floorImageUrlsByFloor],
  );
  const floorRowsToRender = useMemo(() => buildFloorRows(floorTilesToRender), [floorTilesToRender]);

  const hasAnyFloorImage = floorTilesToRender.length > 0;
  const selectedFloorsCount = selectedFloorNumbers.length;
  const isSingleFloorLayout = selectedFloorsCount === 1;
  const shouldFillRemainingHeight = isSingleFloorLayout && !isScrollableMobileMap;
  const mobileMapViewportHeight = isMobileLandscape ? 'calc(100dvh - 12px)' : 'calc(100dvh - 220px)';
  const markerScaleFactor = useMemo(() => {
    if (selectedFloorsCount <= 1) return 1;
    if (selectedFloorsCount === 2) return 0.76;
    if (selectedFloorsCount === 3) return 0.72;
    if (selectedFloorsCount === 4) return 0.7;
    if (selectedFloorsCount === 5) return 0.62;
    return 0.6;
  }, [selectedFloorsCount]);
  const baseMarkerScale = isSmallScreen ? 0.8 : 1;
  const hoverMarkerScale = baseMarkerScale * 1.15;

  useEffect(() => {
    setSelectedFloorsForMap([]);
  }, [building]);

  useEffect(() => {
    if (!building || building === 'all' || availableBuildingFloorNumbers.length === 0) {
      return;
    }

    if (isMultiFloorEnabled) {
      if (!selectedFloorsForMap.length) {
        setSelectedFloorsForMap([availableBuildingFloorNumbers[0]]);
      }
      return;
    }

    if (!floor || floor === 'all') {
      dispatch(setSelectedFloor(availableBuildingFloorNumbers[0]));
    }
  }, [building, floor, isMultiFloorEnabled, availableBuildingFloorNumbers, selectedFloorsForMap.length, dispatch]);

  useEffect(() => {
    if (!isMultiFloorEnabled) {
      setSelectedFloorsForMap([]);
    }
  }, [isMultiFloorEnabled]);

  const handleFloorsForMapChange = useCallback(nextFloors => {
    const limitedFloors = Array.isArray(nextFloors) ? nextFloors.slice(0, MAX_SELECTED_FLOORS) : [];
    setSelectedFloorsForMap(limitedFloors);
  }, []);

  const handleDeviceClick = device => {
    if (isReportMode) {
      setSelectedReportDeviceId(device?.id ?? null);
      setSelectedReportDeviceName(device?.name || `${device?.id || ''}`);
      setReportOccupancy('');
      setReportComment('');
      setIsReportModalOpen(true);
      return;
    }

    const sensorId = device.id;
    if (deviceType === 'controllers') {
      navigate(`/controller/${sensorId}`);
      return;
    }

    navigate(`/data-explorer/${sensorId}`);
  };

  const handleCloseReportModal = () => {
    setIsReportModalOpen(false);
  };

  const handleSubmitReportResult = ({ success, message }) => {
    if (success) {
      setIsReportModalOpen(false);
    }
    setReportSnackbar({
      open: true,
      severity: success ? 'success' : 'error',
      message,
    });
  };

  const handleReportModeToggle = () => {
    setIsReportMode(prev => {
      const next = !prev;
      if (!next) {
        setIsReportModalOpen(false);
      }
      return next;
    });
  };

  const getS3ImageKeyForFloor = useCallback(
    floorNumber => {
      if (!selectedBuilding || !selectedBuilding.floors || !floorNumber) {
        return null;
      }

      const selectedFloor = selectedBuilding.floors.find(f => String(f.floor) === String(floorNumber));
      if (!selectedFloor || !selectedFloor.scheme) {
        return null;
      }

      return selectedFloor.scheme;
    },
    [selectedBuilding],
  );

  useEffect(() => {
    let isCancelled = false;

    const fetchFloorImages = async () => {
      if (!selectedBuilding || selectedFloorNumbers.length === 0) {
        setFloorImageUrlsByFloor({});
        setFloorImageAspectRatiosByFloor({});
        setImageLoading(false);
        return;
      }

      setImageLoading(true);
      try {
        const entries = await Promise.all(
          selectedFloorNumbers.map(async floorNumber => {
            const imageKey = getS3ImageKeyForFloor(floorNumber);
            if (!imageKey) {
              return [String(floorNumber), null];
            }

            try {
              const imageUrl = await fetchS3ImageUrl(imageKey, apiUrl);
              return [String(floorNumber), imageUrl || null];
            } catch (error) {
              console.error(`Error fetching S3 image URL for floor ${floorNumber}:`, error);
              return [String(floorNumber), null];
            }
          }),
        );

        if (!isCancelled) {
          setFloorImageUrlsByFloor(Object.fromEntries(entries));
          setFloorImageAspectRatiosByFloor({});
        }
      } finally {
        if (!isCancelled) {
          setImageLoading(false);
        }
      }
    };

    fetchFloorImages();

    return () => {
      isCancelled = true;
    };
  }, [selectedBuilding, selectedFloorNumbers, getS3ImageKeyForFloor]);

  return (
    <>
      <GlobalStyles
        styles={{
          '@media (orientation: landscape) and (max-height: 600px)': {
            html: {
              overflowY: 'auto',
            },
            body: {
              overflowY: 'auto',
            },
            '.MuiAppBar-root.navbar': {
              position: 'static !important',
            },
          },
        }}
      />
      <DashboardLayout>
        <DashboardNavbar objectName="Floors map" />
        <Typography
          fontSize="11px"
          sx={{
            paddingLeft: { xs: 0, sm: '12px' },
            marginTop: { xs: '-15px', sm: '-5px' },
            textAlign: { xs: 'center', sm: 'left' },
          }}>
          Smart indoor air monitoring & management platform
        </Typography>
        <MDBox pt={{ xs: 1 }} pb={0} className="body-container">
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <MDBox
                mb={1}
                px={2}
                variant="gradient"
                borderRadius="lg"
                coloredShadow="light"
                id="topBar"
                sx={{
                  position: 'relative',
                  zIndex: 1200,
                }}
                style={{
                  background: '#DEE4F9',
                }}>
                <Box
                  className="top-bar-container"
                  sx={{
                    display: { xs: 'flex', xl: 'grid' },
                    gridTemplateColumns: { xl: '1fr auto 1fr' },
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: { xs: 1, sm: 0 },
                    pb: { xs: 0 },
                  }}>
                  {/* Left section */}
                  <Stack
                    direction="row"
                    marginLeft={{ xs: 0, sm: 2 }}
                    spacing={2}
                    alignItems="center"
                    className="top-bar"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: { xs: 'center', lg: 'flex-start' },
                      width: { xs: '100%', lg: 'auto' },
                    }}>
                    <MDTypography variant="h4" color="dark" sx={{ textAlign: { xs: 'center', lg: 'left' } }}>
                      Floors
                    </MDTypography>
                  </Stack>

                  {/* Center section - DevicesListFilter */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', xl: 'row' },
                      alignItems: { xs: 'stretch', lg: 'center' },
                      gap: { xs: 1, xl: 0 },
                      ml: { xl: 4 },
                      width: { xs: '100%', xl: 'auto' },
                    }}>
                    <DevicesListFilter
                      area={false}
                      status={false}
                      search={false}
                      hvacEquipment
                      multiFloorSelect={isMultiFloorEnabled}
                      selectedFloors={isMultiFloorEnabled ? selectedFloorsForMap : []}
                      onFloorsChange={handleFloorsForMapChange}
                      maxMultiFloorSelections={MAX_SELECTED_FLOORS}
                      hideAllFloorOption
                      smMdColumns={3}
                      hvacEquipmentItemSx={{ ml: { lg: 1 } }}
                    />

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          sm: 'repeat(2, minmax(0, 1fr))',
                          lg: 'repeat(4, 182px)',
                        },
                        justifyContent: 'center',
                        gap: 1,
                        width: { xs: 'calc(100% - 8px)', sm: '100%', lg: 'auto' },
                        mx: { xs: '.5rem', sm: 0, xl: 1 },
                      }}>
                      <ToggleButtonGroup
                        exclusive
                        value={deviceType}
                        onChange={handleDeviceTypeChange}
                        sx={{
                          height: '46px',
                          width: { xs: '100%', lg: 182 },
                          '& .MuiToggleButton-root': {
                            height: '46px',
                            minHeight: '46px',
                            minWidth: 0,
                            width: '50%',
                            flex: 1,
                          },
                        }}
                        aria-label="device type toggle">
                        <ToggleButton
                          value="sensors"
                          aria-label="sensors"
                          sx={{
                            px: 1,
                            gap: 0.8,
                            height: '46px',
                            minHeight: '46px',
                            textTransform: 'none',
                            color: '#000',
                            fontWeight: '600',
                            border: `1px solid ${brandColors.BLUE}`,
                            borderRadius: '0.375rem',
                            '&.Mui-selected, &.Mui-selected:hover': {
                              backgroundColor: brandColors.BLUE,
                              color: '#fff',
                            },
                          }}>
                          <Tooltip title="Sensors" placement="top">
                            <Box component="span" sx={{ display: 'inline-flex' }}>
                              <SensorsIcon sx={{ scale: '2.5' }} />
                            </Box>
                          </Tooltip>
                        </ToggleButton>
                        <ToggleButton
                          value="controllers"
                          aria-label="controllers"
                          sx={{
                            px: 1,
                            gap: 0.3,
                            height: '46px',
                            minHeight: '46px',
                            textTransform: 'none',
                            color: '#000',
                            fontWeight: '600',
                            border: `1px solid ${brandColors.BLUE}`,
                            borderRadius: '0.375rem',
                            fontSize: '.9rem',
                            '& .controller-icon': {
                              flexShrink: 0,
                              width: { xs: 28 },
                              height: { xs: 28 },
                            },
                            '& .controller-icon path': {
                              fill: 'currentColor',
                            },
                            '&.Mui-selected, &.Mui-selected:hover': {
                              backgroundColor: brandColors.BLUE,
                              color: '#fff',
                            },
                          }}>
                          <Tooltip title="Controllers" placement="top">
                            <Box component="span" sx={{ display: 'inline-flex' }}>
                              <ControllerBoxIcon className="controller-icon" />
                            </Box>
                          </Tooltip>
                        </ToggleButton>
                      </ToggleButtonGroup>
                      {deviceType !== 'controllers' && (
                        <>
                          <Box
                            className="sensors-info-dropdown"
                            sx={{
                              width: { xs: '100%', lg: 182 },
                              mt: isSmallScreen ? '.8rem' : 0,
                            }}>
                            {/* Multi-select for choosing which sensor measurements can rotate on the map. */}
                            <FormControl ref={measurementSelectRef} fullWidth sx={{ py: 0 }}>
                              <InputLabel id="floor-map-measurement-label">Measurement</InputLabel>
                              <Select
                                labelId="floor-map-measurement-label"
                                multiple
                                value={selectedMeasurements}
                                label="Measurement"
                                onChange={handleMeasurementTypesChange}
                                MenuProps={{
                                  disableScrollLock: true,
                                  PaperProps: {
                                    sx: {
                                      width: measurementMenuWidth || undefined,
                                      maxWidth: measurementMenuWidth || undefined,
                                      mt: '-15px',
                                    },
                                  },
                                }}
                                // Show the measurement currently displayed, even when several are selected.
                                renderValue={() => (selectedMeasurements.length ? displayedMeasurementLabel : '')}
                                sx={{
                                  height: '46px',
                                  borderRadius: '0.375rem',
                                }}>
                                {measurementsOpts.map(option => (
                                  <MenuItem
                                    key={option.value}
                                    value={option.value}
                                    sx={{
                                      '&.MuiMenuItem-root': {
                                        height: '38px',
                                        minHeight: '38px !important',
                                        maxHeight: '38px',
                                        py: 0,
                                        px: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                      },
                                    }}>
                                    <Checkbox
                                      checked={selectedMeasurements.includes(option.value)}
                                      sx={{ width: '32px', height: '32px', p: 0.5, mr: 0.5 }}
                                    />
                                    <ListItemText
                                      sx={{
                                        minWidth: 0,
                                        height: '100%',
                                        my: 0,
                                        overflow: 'hidden',
                                        display: 'flex',
                                        alignItems: 'center',
                                      }}
                                      primaryTypographyProps={{
                                        component: 'div',
                                        sx: { display: 'flex', alignItems: 'center', width: '100%' },
                                      }}
                                      primary={<MarqueeText animationDuration="2s">{option.name}</MarqueeText>}
                                    />
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                          <Box
                            className="building-demand-dropdown"
                            sx={{
                              width: { xs: '100%', lg: 182 },
                              mt: isSmallScreen ? '.8rem' : 0,
                            }}>
                            <FormControl ref={buildingDemandSelectRef} fullWidth sx={{ py: 0 }}>
                              <InputLabel id="floor-map-building-demand-label" sx={{ lineHeight: 1.2 }}>
                                Building demand
                              </InputLabel>
                              <Select
                                labelId="floor-map-building-demand-label"
                                multiple
                                value={selectedDemandMeasurements}
                                label="Building demand"
                                onChange={event => {
                                  const value = event.target.value;
                                  const requestedDemandMeasurements = typeof value === 'string' ? value.split(',') : value;
                                  const nextMeasurements =
                                    requestedDemandMeasurements.length || selectedMeasurements.length
                                      ? selectedMeasurements
                                      : [measurementsOpts[0].value];
                                  setSelectedMeasurements(nextMeasurements);
                                  setSelectedDemandMeasurements(requestedDemandMeasurements);
                                  setSelectedMeasurementTypes([
                                    ...new Set([...nextMeasurements, ...requestedDemandMeasurements]),
                                  ]);
                                }}
                                MenuProps={{
                                  disableScrollLock: true,
                                  PaperProps: {
                                    sx: {
                                      width: buildingDemandMenuWidth || undefined,
                                      maxWidth: buildingDemandMenuWidth || undefined,
                                      mt: '-15px',
                                    },
                                  },
                                }}
                                renderValue={selected =>
                                  selected
                                    .map(field => demandMeasurements.find(measurement => measurement.field === field)?.label)
                                    .filter(Boolean)
                                    .join(', ')
                                }
                                sx={{
                                  height: '46px',
                                  borderRadius: '0.375rem',
                                }}>
                                {demandMeasurements.map(measurement => (
                                  <MenuItem
                                    key={measurement.field}
                                    value={measurement.field}
                                    sx={{
                                      '&.MuiMenuItem-root': {
                                        minHeight: '38px',
                                        py: 0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        px: 0,
                                      },
                                    }}>
                                    <Checkbox
                                      checked={selectedDemandMeasurements.includes(measurement.field)}
                                      sx={{ width: '32px', height: '32px', p: 0.5, mr: 0.5 }}
                                    />
                                    <ListItemText
                                      sx={{
                                        minWidth: 0,
                                        my: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                      }}
                                      primaryTypographyProps={{
                                        component: 'div',
                                        sx: {
                                          display: 'flex',
                                          alignItems: 'center',
                                          width: '100%',
                                          color: measurement.color,
                                          fontSize: '.9rem',
                                          whiteSpace: 'normal',
                                          wordBreak: 'break-word',
                                        },
                                      }}
                                      primary={measurement.label}
                                    />
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                          {selectedMeasurementTypes.length > 1 && (
                            <Box
                              className="measurement-rotation-dropdown"
                              sx={{
                                width: { xs: '100%', lg: 182 },
                                mt: isSmallScreen ? '.8rem' : 0,
                              }}>
                              {/* Rotation speed only matters when more than one measurement is selected. */}
                              <DropdownMenuVertical
                                labelName="Switch every"
                                options={measurementRotationOptions}
                                selectedOption={measurementRotationSeconds}
                                handleChange={e => {
                                  setMeasurementRotationSeconds(Number(e.target.value));
                                }}
                                width="100%"
                              />
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>

                  {/* Right section placeholder */}
                  <Box />
                </Box>
              </MDBox>
            </Grid>
          </Grid>
          {imageLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                height: '70vh',
                alignItems: 'center',
              }}>
              <Typography>Loading floor image...</Typography>
            </Box>
          ) : hasAnyFloorImage && devices && devices.length > 0 ? (
            <Box
              sx={{
                width: '100%',
                overflowY: 'visible',
              }}>
              <Box
                sx={{
                  width: '100%',
                  maxHeight: isScrollableMobileMap ? mobileMapViewportHeight : 'none',
                  height: isScrollableMobileMap ? mobileMapViewportHeight : 'auto',
                  overflowX: isScrollableMobileMap ? 'auto' : 'visible',
                  overflowY: isScrollableMobileMap ? 'auto' : 'visible',
                  WebkitOverflowScrolling: 'touch',
                }}>
                <Box
                  sx={{
                    position: 'relative',
                    width: isScrollableMobileMap ? 'max-content' : { xs: '88vw', xl: '82vw' },
                    minWidth: isScrollableMobileMap ? '100%' : 0,
                    mx: 'auto',
                    pb: 0,
                    height: shouldFillRemainingHeight ? 'calc(100dvh - 245px)' : 'auto',
                  }}>
                  {/* Header for the measurement currently shown on every sensor marker. */}
                  {deviceType !== 'controllers' && !isReportMode && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '.5rem',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 221,
                        px: { xs: 1.2, sm: 1.6 },
                        py: { xs: 0.45, sm: 0.6 },
                        borderRadius: '.4rem',
                        backgroundColor: 'rgba(255, 255, 255, 0.92)',
                        border: '1px solid rgba(0, 0, 0, 0.12)',
                        boxShadow: '0 4px 14px rgba(0, 0, 0, 0.12)',
                        maxWidth: { xs: 'calc(100% - 8rem)', sm: '60%' },
                      }}>
                      <Typography
                        sx={{
                          fontSize: { xs: '.95rem', sm: '1.15rem' },
                          fontWeight: 700,
                          color: displayedMeasurementLabelColor,
                          lineHeight: 1.2,
                          textAlign: 'center',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                        {displayedMeasurementLabel}
                      </Typography>
                    </Box>
                  )}
                  {/* <Button
                    type="button"
                    className="report-btn"
                    onClick={handleReportModeToggle}
                    sx={{
                      scale: {
                        xs: 0.7,
                        sm: 1,
                      },
                      position: 'absolute',
                      top: '.5rem',
                      right: '.5rem',
                      zIndex: 220,
                      p: '0.5rem 0.5rem 0.5rem 0rem',
                      height: '46px',
                      minHeight: '46px',
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      color: '#000',
                      fontWeight: '600',
                      border: `1px solid ${brandColors.BLUE}`,
                      borderRadius: '0.375rem',
                      backgroundColor: '#fff',
                      '&:hover': {
                        backgroundColor: '#f6f8ff',
                        border: `1px solid ${brandColors.BLUE}`,
                      },
                      '& .report-icon path': {
                        fill: 'currentColor',
                      },
                    }}>
                    <OccupancyIcon className="report-icon" width={38} height={38} />
                    {isReportMode ? 'Cancel' : 'Report'}
                  </Button> */}

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.2,
                      height: shouldFillRemainingHeight ? '100%' : 'auto',
                    }}>
                    {floorRowsToRender.map((row, rowIndex) => (
                      <Box
                        key={`floor-row-${rowIndex}`}
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: 1.2,
                          width: '100%',
                          height: shouldFillRemainingHeight ? '100%' : 'auto',
                          justifyContent:
                            row.length === 1 || (floorTilesToRender.length === 3 && rowIndex === 1) ? 'center' : 'flex-start',
                        }}>
                        {row.map(tile => {
                          const imageUrl = floorImageUrlsByFloor[tile.floorNumber];
                          if (!imageUrl) {
                            return null;
                          }

                          const floorDevices = displayedDevices.filter(
                            device => String(device?.floor_number ?? device?.floor ?? '') === String(tile.floorNumber),
                          );

                          const tileWidth = floorTilesToRender.length === 3 && row.length === 1 ? '50%' : `${100 / row.length}%`;
                          const imageAspectRatio = floorImageAspectRatiosByFloor[tile.floorNumber] || '16 / 10';
                          const responsiveTileWidth = isMobileLandscape ? '100%' : isSmallScreen ? '180vw' : tileWidth;

                          return (
                            <Box
                              key={`floor-tile-${tile.floorNumber}`}
                              sx={{
                                width: responsiveTileWidth,
                                height: shouldFillRemainingHeight ? '100%' : 'auto',
                                minWidth: 0,
                              }}>
                              <Box
                                sx={{
                                  position: 'relative',
                                  width: '100%',
                                  height: shouldFillRemainingHeight ? '100%' : 'auto',
                                  aspectRatio: shouldFillRemainingHeight
                                    ? 'auto'
                                    : isScrollableMobileMap
                                      ? imageAspectRatio
                                      : '16 / 10',
                                  overflow: 'visible',
                                  borderRadius: '.4rem',
                                  border: '1px solid rgba(0, 0, 0, 0.12)',
                                  backgroundColor: '#f1f4fa',
                                }}>
                                <Typography
                                  sx={{
                                    position: 'absolute',
                                    top: '.45rem',
                                    left: '.55rem',
                                    zIndex: 106,
                                    fontSize: '.8rem',
                                    fontWeight: 700,
                                    color: '#111',
                                    backgroundColor: 'rgba(255,255,255,0.88)',
                                    borderRadius: '.35rem',
                                    px: '.4rem',
                                    py: '.05rem',
                                  }}>
                                  Floor {tile.floorNumber}
                                </Typography>
                                {showGrid && (
                                  <Box
                                    component="img"
                                    src={`/scheme/grid.png`}
                                    alt={`floor ${tile.floorNumber} grid`}
                                    sx={{
                                      position: 'absolute',
                                      inset: 0,
                                      width: '100%',
                                      height: '100%',
                                      zIndex: 100,
                                    }}
                                  />
                                )}
                                <Box
                                  component="img"
                                  src={imageUrl}
                                  alt={`floor ${tile.floorNumber} image`}
                                  onLoad={event => {
                                    const { naturalWidth, naturalHeight } = event.currentTarget;
                                    if (naturalWidth > 0 && naturalHeight > 0) {
                                      const nextAspectRatio = `${naturalWidth} / ${naturalHeight}`;
                                      setFloorImageAspectRatiosByFloor(prev =>
                                        prev[tile.floorNumber] === nextAspectRatio
                                          ? prev
                                          : {
                                              ...prev,
                                              [tile.floorNumber]: nextAspectRatio,
                                            },
                                      );
                                    }
                                  }}
                                  sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    width: '100%',
                                    height: '100%',
                                    display: 'block',
                                    objectFit: 'fill',
                                    opacity: 0.7,
                                    transition: 'filter 220ms ease, opacity 220ms ease',
                                    filter: isDeviceFocusActive ? 'blur(2px)' : 'none',
                                  }}
                                />
                                {floorDevices.map(device => {
                                  const rawLastReadingTime =
                                    deviceType === 'controllers'
                                      ? device.controller_last_reading_time
                                      : device.sensor_last_reading_time;
                                  const deviceLastReadingTime = Number(rawLastReadingTime ?? device.last_reading_time ?? 0);
                                  const readingAgeSeconds = Date.now() / 1000 - deviceLastReadingTime;
                                  const isInactiveByLastReading =
                                    !(deviceLastReadingTime > 0) || readingAgeSeconds > INACTIVE_TIME_IN_SECONDS;
                                  const hasFreshReading = !isInactiveByLastReading;
                                  const isHoveredDevice = hoveredDeviceId === device.id;
                                  const measurementValue = Number(device[displayedMeasurementType]);
                                  const shouldShowMeasurementText = measurementValue !== -1;
                                  const showColdTemperatureIcon =
                                    displayedMeasurementType === 'temperature_avg' &&
                                    shouldShowMeasurementText &&
                                    hasFreshReading &&
                                    Number.isFinite(measurementValue) &&
                                    measurementValue < 21;
                                  const showHotTemperatureIcon =
                                    displayedMeasurementType === 'temperature_avg' &&
                                    shouldShowMeasurementText &&
                                    hasFreshReading &&
                                    Number.isFinite(measurementValue) &&
                                    measurementValue > 26;
                                  return (
                                    <Box
                                      key={`${tile.floorNumber}-${device.id}`}
                                      className="device-object"
                                      sx={{
                                        position: 'absolute',
                                        opacity: 1,
                                        left: `${device.map_x_value}%`,
                                        top: `${device.map_y_value}%`,
                                        cursor: 'pointer',
                                        zIndex: isHoveredDevice ? 103 : 101,
                                        width: 'fit-content',
                                        minHeight: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transform: `translate(-50%, -50%) scale(${baseMarkerScale * markerScaleFactor})`,
                                        transformOrigin: 'center center',
                                        filter: isDeviceFocusActive && !isHoveredDevice ? 'blur(2px)' : 'none',
                                        transition: 'transform 200ms ease, filter 220ms ease, opacity 220ms ease',
                                        '&:hover': {
                                          transform: `translate(-50%, -50%) scale(${hoverMarkerScale * markerScaleFactor})`,
                                          zIndex: 104,
                                        },
                                        '&:hover .controller-device-image': {
                                          filter: 'drop-shadow(0 6px 8px rgba(0, 0, 0, 0.5))',
                                        },
                                        '& .device-hover-list': {
                                          position: 'absolute',
                                          top: '100%',
                                          left: '50%',
                                          opacity: 0,
                                          transform: 'translate(-50%, -6px)',
                                          maxHeight: 0,
                                          overflow: 'hidden',
                                          pointerEvents: 'none',
                                          transition: 'opacity 220ms ease, transform 220ms ease, max-height 260ms ease',
                                        },
                                        '&:hover .device-hover-list': {
                                          opacity: 1,
                                          transform: 'translate(-50%, 0)',
                                          maxHeight: '220px',
                                        },
                                      }}
                                      onMouseEnter={() => setHoveredDeviceId(device.id)}
                                      onMouseLeave={() => setHoveredDeviceId(null)}
                                      onClick={() => handleDeviceClick(device)}>
                                      <Box
                                        sx={{
                                          position: 'relative',
                                          width: '4rem',
                                          height: '4rem',
                                          display: 'flex',
                                          justifyContent: 'center',
                                        }}>
                                        <Box
                                          component="img"
                                          className={`sensor-object ${deviceType === 'controllers' ? 'controller-device-image' : ''}`}
                                          src={Sensor}
                                          alt="sensorImage"
                                          sx={{
                                            width: '4rem',
                                            filter: isInactiveByLastReading
                                              ? buildGrayGlow()
                                              : deviceType === 'controllers'
                                                ? 'drop-shadow(0 2px 3px rgba(0, 0, 0, 0.3))'
                                                : getSensorGlow(device[displayedMeasurementType]),
                                            transition: 'filter 200ms ease',
                                          }}
                                        />
                                        {isInactiveByLastReading && (
                                          <WifiOffIcon
                                            sx={{
                                              position: 'absolute',
                                              top: '50%',
                                              left: '50%',
                                              transform: 'translate(-50%, -50%)',
                                              zIndex: 103,
                                              color: '#000',
                                              filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.9))',
                                              width: '1.5rem',
                                              height: '1.5rem',
                                            }}
                                          />
                                        )}
                                        {deviceType === 'controllers' && !isReportMode && (
                                          <Box
                                            sx={{
                                              position: 'absolute',
                                              inset: 0,
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                            }}>
                                            <Box
                                              component={Fan}
                                              sx={{
                                                width: '1.8rem',
                                                height: '1.8rem',
                                                animation:
                                                  device.on_off === 1 && hasFreshReading
                                                    ? 'controllerFanSpin .8s linear infinite'
                                                    : 'none',
                                                '@keyframes controllerFanSpin': {
                                                  '0%': { transform: 'rotate(0deg)' },
                                                  '100%': { transform: 'rotate(360deg)' },
                                                },
                                              }}
                                            />
                                          </Box>
                                        )}
                                        {isReportMode && (
                                          <Box
                                            sx={{
                                              position: 'absolute',
                                              inset: 0,
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              zIndex: 102,
                                              color: '#111',
                                              '& .report-device-icon path': {
                                                fill: 'currentColor',
                                              },
                                            }}>
                                            <OccupancyIcon width={50} height={50} className="report-device-icon" />
                                          </Box>
                                        )}
                                        {deviceType !== 'controllers' &&
                                          !isReportMode &&
                                          shouldShowMeasurementText &&
                                          !isInactiveByLastReading && (
                                            <Box
                                              className="sensor-value"
                                              sx={{
                                                position: 'absolute',
                                                inset: 0,
                                                display: 'flex',
                                                flexDirection: 'row',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                              }}>
                                              <Typography
                                                align="center"
                                                className="sensor-curr-value"
                                                sx={{
                                                  zIndex: 101,
                                                  fontSize: '.95rem',
                                                  fontWeight: '600',
                                                  color: '#000',
                                                  textShadow:
                                                    'rgb(255, 255, 255) 2px 0px 0px, rgb(255, 255, 255) 1.75517px 0.958851px 0px, rgb(255, 255, 255) 1.0806px 1.68294px 0px, rgb(255, 255, 255) 0.141474px 1.99499px 0px, rgb(255, 255, 255) -0.832294px 1.81859px 0px, rgb(255, 255, 255) -1.60229px 1.19694px 0px, rgb(255, 255, 255) -1.97998px 0.28224px 0px, rgb(255, 255, 255) -1.87291px -0.701566px 0px, rgb(255, 255, 255) -1.30729px -1.5136px 0px, rgb(255, 255, 255) -0.421592px -1.95506px 0px, rgb(255, 255, 255) 0.567324px -1.91785px 0px, rgb(255, 255, 255) 1.41734px -1.41108px 0px, rgb(255, 255, 255) 1.92034px -0.558831px 0px;',
                                                }}>
                                                {`${device[displayedMeasurementType]}`}
                                              </Typography>
                                              <Typography
                                                align="center"
                                                sx={{
                                                  zIndex: 101,
                                                  fontSize: '.7rem',
                                                  fontWeight: '600',
                                                  color: '#000',
                                                  ml: '.2rem',
                                                  textShadow:
                                                    'rgb(255, 255, 255) 2px 0px 0px, rgb(255, 255, 255) 1.75517px 0.958851px 0px, rgb(255, 255, 255) 1.0806px 1.68294px 0px, rgb(255, 255, 255) 0.141474px 1.99499px 0px, rgb(255, 255, 255) -0.832294px 1.81859px 0px, rgb(255, 255, 255) -1.60229px 1.19694px 0px, rgb(255, 255, 255) -1.97998px 0.28224px 0px, rgb(255, 255, 255) -1.87291px -0.701566px 0px, rgb(255, 255, 255) -1.30729px -1.5136px 0px, rgb(255, 255, 255) -0.421592px -1.95506px 0px, rgb(255, 255, 255) 0.567324px -1.91785px 0px, rgb(255, 255, 255) 1.41734px -1.41108px 0px, rgb(255, 255, 255) 1.92034px -0.558831px 0px;',
                                                }}>
                                                {`${getMeasUnit(displayedMeasurementType)}`}
                                              </Typography>
                                              {showColdTemperatureIcon && (
                                                <AcUnitIcon
                                                  sx={{
                                                    zIndex: 101,
                                                    color: brandColors.TURQUOISE,
                                                    ml: '.15rem',
                                                    scale: 1.2,
                                                    filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.95))',
                                                  }}
                                                />
                                              )}
                                              {showHotTemperatureIcon && (
                                                <LocalFireDepartmentIcon
                                                  sx={{
                                                    zIndex: 101,
                                                    color: getTemperatureStatusColor(measurementValue),
                                                    ml: '.15rem',
                                                    scale: 1.2,
                                                    filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.95))',
                                                  }}
                                                />
                                              )}
                                            </Box>
                                          )}
                                      </Box>
                                      <Typography
                                        align="center"
                                        sx={{
                                          zIndex: 101,
                                          fontSize: '1rem',
                                          fontWeight: '600',
                                          borderRadius: '3px',
                                          color: '#000',
                                          whiteSpace: 'pre-line',
                                          textShadow:
                                            'rgb(255, 255, 255) 2px 0px 0px, rgb(255, 255, 255) 1.75517px 0.958851px 0px, rgb(255, 255, 255) 1.0806px 1.68294px 0px, rgb(255, 255, 255) 0.141474px 1.99499px 0px, rgb(255, 255, 255) -0.832294px 1.81859px 0px, rgb(255, 255, 255) -1.60229px 1.19694px 0px, rgb(255, 255, 255) -1.97998px 0.28224px 0px, rgb(255, 255, 255) -1.87291px -0.701566px 0px, rgb(255, 255, 255) -1.30729px -1.5136px 0px, rgb(255, 255, 255) -0.421592px -1.95506px 0px, rgb(255, 255, 255) 0.567324px -1.91785px 0px, rgb(255, 255, 255) 1.41734px -1.41108px 0px, rgb(255, 255, 255) 1.92034px -0.558831px 0px;',
                                        }}>
                                        {formatDeviceNameWithLineBreaks(device.name)}
                                      </Typography>
                                      {deviceType === 'controllers' && (
                                        <Box
                                          className="device-hover-list"
                                          sx={{
                                            zIndex: 101,
                                            width: 'fit-content',
                                            backgroundColor: 'rgba(255, 255, 255, 0.94)',
                                            border: '1px solid rgba(0, 0, 0, 0.12)',
                                            borderRadius: '.45rem',
                                            px: '.45rem',
                                            py: '.3rem',
                                            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.16)',
                                          }}>
                                          <Typography
                                            sx={{
                                              fontSize: '.8rem',
                                              fontWeight: 600,
                                              color: '#1f1f1f',
                                              lineHeight: 1.35,
                                              whiteSpace: 'nowrap',
                                            }}>
                                            Last update: {formatUnixSecondsToDisplay(deviceLastReadingTime)}
                                          </Typography>
                                          <Typography
                                            sx={{
                                              fontSize: '.8rem',
                                              fontWeight: 600,
                                              color: '#1f1f1f',
                                              lineHeight: 1.35,
                                              whiteSpace: 'nowrap',
                                            }}>
                                            Control Mode: {getControlModeLabel(device.control)}
                                          </Typography>
                                          {device.device_type === 'Controller-relay-SEN54' ? (
                                            <>
                                              <Typography
                                                sx={{
                                                  fontSize: '.8rem',
                                                  fontWeight: 600,
                                                  color: '#1f1f1f',
                                                  lineHeight: 1.35,
                                                  whiteSpace: 'nowrap',
                                                }}>
                                                Channel 1:{' '}
                                                {(() => {
                                                  if (!hasFreshReading) return 'N/A';
                                                  if (device.channel_1 === 0) {
                                                    return (
                                                      <Box component="span" sx={{ color: '#d32f2f' }}>
                                                        OFF
                                                      </Box>
                                                    );
                                                  }
                                                  if (device.channel_1 === 1) {
                                                    return (
                                                      <Box component="span" sx={{ color: '#2e7d32' }}>
                                                        ON
                                                      </Box>
                                                    );
                                                  }
                                                  return 'N/A';
                                                })()}
                                              </Typography>
                                              <Typography
                                                sx={{
                                                  fontSize: '.8rem',
                                                  fontWeight: 600,
                                                  color: '#1f1f1f',
                                                  lineHeight: 1.35,
                                                  whiteSpace: 'nowrap',
                                                }}>
                                                Channel 2:{' '}
                                                {(() => {
                                                  if (!hasFreshReading) return 'N/A';
                                                  if (device.channel_2 === 0) {
                                                    return (
                                                      <Box component="span" sx={{ color: '#d32f2f' }}>
                                                        OFF
                                                      </Box>
                                                    );
                                                  }
                                                  if (device.channel_2 === 1) {
                                                    return (
                                                      <Box component="span" sx={{ color: '#2e7d32' }}>
                                                        ON
                                                      </Box>
                                                    );
                                                  }
                                                  return 'N/A';
                                                })()}
                                              </Typography>
                                            </>
                                          ) : (
                                            <>
                                              <Typography
                                                sx={{
                                                  fontSize: '.8rem',
                                                  fontWeight: 600,
                                                  color: '#1f1f1f',
                                                  lineHeight: 1.35,
                                                  whiteSpace: 'nowrap',
                                                }}>
                                                Power:{' '}
                                                {(() => {
                                                  if (!hasFreshReading) return 'N/A';
                                                  if (device.on_off === 0) {
                                                    return (
                                                      <Box component="span" sx={{ color: '#d32f2f' }}>
                                                        OFF
                                                      </Box>
                                                    );
                                                  }
                                                  if (device.on_off === 1) {
                                                    return (
                                                      <Box component="span" sx={{ color: '#2e7d32' }}>
                                                        ON
                                                      </Box>
                                                    );
                                                  }
                                                  return 'N/A';
                                                })()}
                                              </Typography>
                                              <Typography
                                                sx={{
                                                  fontSize: '.8rem',
                                                  fontWeight: 600,
                                                  color: '#1f1f1f',
                                                  lineHeight: 1.35,
                                                  whiteSpace: 'nowrap',
                                                }}>
                                                Mode:{' '}
                                                {!hasFreshReading
                                                  ? 'N/A'
                                                  : device.mode === 'fan'
                                                    ? 'FanOnly𖣘'
                                                    : device.mode === 'cool'
                                                      ? 'Cool❄️'
                                                      : device.mode === 'heat'
                                                        ? 'Heat☀️'
                                                        : device.mode === 'auto'
                                                          ? 'AutoChange'
                                                          : 'N/A'}
                                              </Typography>
                                              <Typography
                                                sx={{
                                                  fontSize: '.8rem',
                                                  fontWeight: 600,
                                                  color: '#1f1f1f',
                                                  lineHeight: 1.35,
                                                  whiteSpace: 'nowrap',
                                                }}>
                                                Temp set point:{' '}
                                                {!hasFreshReading
                                                  ? 'N/A'
                                                  : device.temp_set_point == null
                                                    ? 'N/A'
                                                    : `${device.temp_set_point}°C`}
                                              </Typography>
                                              <Typography
                                                sx={{
                                                  fontSize: '.8rem',
                                                  fontWeight: 600,
                                                  color: '#1f1f1f',
                                                  lineHeight: 1.35,
                                                  whiteSpace: 'nowrap',
                                                }}>
                                                Comfort: {device.comfort || 'N/A'}
                                              </Typography>
                                              <Typography
                                                sx={{
                                                  fontSize: '.8rem',
                                                  fontWeight: 600,
                                                  color: '#1f1f1f',
                                                  lineHeight: 1.35,
                                                  whiteSpace: 'nowrap',
                                                }}>
                                                Cool water:{' '}
                                                {!hasFreshReading
                                                  ? 'N/A'
                                                  : device.cool_water_valve == null
                                                    ? 'N/A'
                                                    : `${device.cool_water_valve}%`}
                                              </Typography>
                                            </>
                                          )}
                                        </Box>
                                      )}
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                height: '70vh',
                // alignItems: 'center',
              }}>
              <EmptyStateMessage
                message={
                  !devices || devices.length === 0
                    ? 'Loading devices data...'
                    : !buildings || buildings.length === 0
                      ? 'Loading buildings data...'
                      : !building || building === 'all' || selectedFloorNumbers.length === 0
                        ? 'Please select a building and one or more floors to view the floor map'
                        : 'One or more selected floors have no map image configured.'
                }
                maxHeight="10rem"
                width="30rem"
              />
            </Box>
          )}
        </MDBox>
        <GenericModal
          open={isReportModalOpen}
          onClose={handleCloseReportModal}
          title={selectedReportDeviceName}
          Component={ReportDeviceModalContent}
          componentProps={{
            userId: currentUser?.user_id || currentUser?.id,
            deviceId: selectedReportDeviceId,
            occupancyValue: reportOccupancy,
            commentValue: reportComment,
            onOccupancyChange: event => setReportOccupancy(event.target.value),
            onCommentChange: event => setReportComment(event.target.value),
            onSubmitted: handleSubmitReportResult,
          }}
        />
        <CustomSnackbar
          open={reportSnackbar.open}
          onClose={() => setReportSnackbar(prev => ({ ...prev, open: false }))}
          severity={reportSnackbar.severity}
          message={reportSnackbar.message}
          duration={3500}
          vertical="bottom"
          horizontal="center"
          color="#fff"
        />
      </DashboardLayout>
    </>
  );
};

export default FloorsMap;
